/**
 * Client-side-only Graph auth for the member-list fetch (BUILD-SPEC.md §6).
 *
 * No server, no secret: this is a public-client PKCE flow. The parent app
 * (running as a Teams tab/panel) never talks to AAD directly — instead it
 * asks teams-js to open a popup via `microsoftTeams.authentication.authenticate()`,
 * and that popup is *this same app*, reloaded with `?authPopup=1` so
 * `main.tsx` knows to run `completeAuthPopup()` instead of mounting the
 * standup UI. Inside the popup, MSAL does the actual AAD redirect dance and
 * reports the result back to the parent via `notifySuccess`/`notifyFailure`.
 *
 * Every failure mode (consent refused, popup blocked, offline, tenant not
 * consented) surfaces as a rejected promise from `getGraphToken()`. Callers
 * (src/roster/members.ts) treat that as an expected path, never an
 * exception — see BUILD-SPEC.md §6.
 */
import * as microsoftTeams from "@microsoft/teams-js";
import { PublicClientApplication } from "@azure/msal-browser";

/** Delegated scope only — no application permissions (BUILD-SPEC.md §8). */
const GRAPH_SCOPE = "ChatMember.Read";
const AUTH_POPUP_PARAM = "authPopup";

/**
 * AAD app (client) id for this Teams app's public-client registration. Set
 * at build time; there is deliberately no fallback secret or server-issued
 * value — if this is empty, `getGraphToken` will fail fast and the caller
 * falls back to the empty-tick-list path, which is the correct behaviour
 * (BUILD-SPEC.md §6, §10: consent may need tenant-wide approval that never
 * lands).
 */
function clientId(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.["VITE_AAD_CLIENT_ID"] ?? "";
}

let msalInstance: PublicClientApplication | null = null;
function getMsal(): PublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: clientId(),
        authority: "https://login.microsoftonline.com/common",
      },
    });
  }
  return msalInstance;
}

function popupUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.set(AUTH_POPUP_PARAM, "1");
  url.hash = "";
  return url.toString();
}

/** True when this window is the teams-js auth popup, not the main app.
 * Checked once by main.tsx at startup. */
export function isAuthPopup(): boolean {
  return new URLSearchParams(window.location.search).get(AUTH_POPUP_PARAM) === "1";
}

/**
 * Called from the main app. Opens the popup and resolves with a Graph
 * access token, or rejects on any failure — consent refused, the popup
 * being blocked by the browser, the user closing it, etc. Never throws
 * synchronously.
 */
export async function getGraphToken(): Promise<string> {
  if (!clientId()) {
    throw new Error("no AAD client id configured for the member-list fetch");
  }
  const token = await microsoftTeams.authentication.authenticate({
    url: popupUrl(),
    width: 600,
    height: 535,
  });
  if (!token) {
    throw new Error("auth popup returned no token");
  }
  return token;
}

/**
 * Runs *inside* the popup window only (main.tsx checks `isAuthPopup()` and
 * calls this instead of mounting the React app). Drives MSAL's redirect
 * flow within the popup itself — never opens a second, nested popup — and
 * reports the outcome back to the parent frame via teams-js.
 */
export async function completeAuthPopup(): Promise<void> {
  const msal = getMsal();
  try {
    const redirectResult = await msal.handleRedirectPromise();
    if (redirectResult?.accessToken) {
      microsoftTeams.authentication.notifySuccess(redirectResult.accessToken);
      return;
    }

    const account = msal.getAllAccounts()[0];
    if (account) {
      const silent = await msal.acquireTokenSilent({ scopes: [GRAPH_SCOPE], account });
      microsoftTeams.authentication.notifySuccess(silent.accessToken);
      return;
    }

    // First load of the popup: kick off the redirect. The popup will
    // navigate to AAD and back to this same URL, at which point
    // `handleRedirectPromise` above resolves with the token.
    await msal.loginRedirect({ scopes: [GRAPH_SCOPE] });
  } catch (err) {
    microsoftTeams.authentication.notifyFailure(describeAuthError(err));
  }
}

function describeAuthError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
